import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { RecoilState, useRecoilState, useSetRecoilState } from 'recoil';
import { ReactComponent as EditIcon } from '../../../../assets/16x16/edit.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/arrow-right.svg';
import useDataTestsEnabled from '../../../../hooks/useDataTestsEnabled';
import useNavigateWithEnvParam from '../../../../hooks/useNavigateWithEnvParam';
import { aiChatPanel } from '../../../../atoms/atoms';
import type {
  AgentTestChangeRowIdentifiers,
  AgentTestConfigPlan,
  AgentTestOperation,
  PlanApprovalStatus,
} from '../../../../types';
import { getAgentTestChangeRowId } from '../../../../types';
import type { ApprovalSubmissionResult, ToolRendererArgs } from '../../types';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { Flex } from '../../../Flex';
import Button from '../../../Button';
import SvgIcon from '../../../SvgIcon';
import {
  ApprovalCard,
  ApprovalCardBody,
  ApprovalCardHeader,
  ApprovalCardTitle,
} from '../approvalCard';
import {
  neverReachedApproval,
  resolveCompletedStatus,
} from '../planApprovalStatus';
import { themeTokens } from '../../../../style/tokens';
import { fontSize, fontWeight, space } from '../../../../style/utils';
import {
  buildNewArgsFromPlan,
  formatSkippedPlanSummary,
  getChangesFromToolArgs,
  getEnvIdFromToolArgs,
  isAgentTestOperation,
  planFromToolArgs,
} from './sharedUtils';

const MAX_PREVIEW_ROWS = 3;
const MAX_RESULT_UNWRAP_DEPTH = 4;

// The session infra wraps a tool result as { result: <toolResult>, isError }
// where the outer isError is the envelope's own flag (always false for a tool
// that ran). The real approval outcome lives on the nested tool result, so walk
// down the `result` chain and return the deepest isError we find.
const extractToolResultIsError = (raw: unknown): boolean | undefined => {
  let current: unknown = raw;
  let isError: boolean | undefined;
  for (let depth = 0; depth < MAX_RESULT_UNWRAP_DEPTH; depth += 1) {
    if (!current || typeof current !== 'object') break;
    const record = current as Record<string, unknown>;
    if (typeof record.isError === 'boolean') {
      isError = record.isError;
    }
    current = record.result;
  }
  return isError;
};

const ViewLink = styled(Flex)`
  align-items: center;
  gap: ${space('xs')};
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.accent.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ChangesGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(0, max-content);
  column-gap: ${space('sm')};
  gap: ${space('sm')};
  width: 100%;
`;

const TestIdCell = styled(Typography)`
  font-size: ${fontSize('md')};
  font-weight: ${fontWeight('medium')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export type PlanRendererConfig<TChange extends AgentTestChangeRowIdentifiers> =
  {
    planAtom: RecoilState<AgentTestConfigPlan<TChange> | null>;
    previewPath: string;
    returnPath: string;
    formatChangeLabel: (change: TChange) => string;
    formatChangeSummary: (
      change: TChange,
      operation: AgentTestOperation,
    ) => string;
    getHeaderLabel: (
      status: PlanApprovalStatus | undefined,
      operation: AgentTestOperation,
      count: number,
    ) => string;
    getApproveLabel: (
      operation: AgentTestOperation,
      changes: TChange[],
      isCustomSqlCloudEnabled?: boolean,
    ) => string;
    // Lets a plan type derive row identity the agent's arguments don't carry.
    normalizeChanges?: (changes: TChange[]) => TChange[];
  };

type InnerProps<TChange extends AgentTestChangeRowIdentifiers> = {
  config: PlanRendererConfig<TChange>;
  toolArgs: ToolRendererArgs;
  toolCallId: string;
  resultIsError?: boolean;
  didAwaitApproval?: boolean;
  onApproveToolUse: (
    approved: boolean,
    newArgs?: Record<string, unknown>,
  ) => Promise<ApprovalSubmissionResult>;
  isActive?: boolean;
  interactionsDisabled?: boolean;
};

function ApplyTestConfigPlanToolRendererInner<
  TChange extends AgentTestChangeRowIdentifiers,
>({
  config,
  toolArgs,
  toolCallId,
  resultIsError,
  didAwaitApproval,
  onApproveToolUse,
  isActive,
  interactionsDisabled,
}: InnerProps<TChange>) {
  const navigate = useNavigateWithEnvParam();
  const location = useLocation();
  const isDataTestsEnabled = useDataTestsEnabled(
    getEnvIdFromToolArgs(toolArgs),
  );
  const [plan, setPlan] = useRecoilState(config.planAtom);
  const setChatPanel = useSetRecoilState(aiChatPanel);
  const responseRef = useRef<PlanApprovalStatus | null>(null);
  const responseInFlightRef = useRef(false);

  const closePreviewIfOpen = useCallback(() => {
    if (location.pathname.endsWith(config.previewPath)) {
      navigate(config.returnPath);
    }
  }, [location.pathname, navigate, config.previewPath, config.returnPath]);

  const operation = isAgentTestOperation(toolArgs?.operation)
    ? toolArgs.operation
    : 'update';
  const changes = useMemo(
    () => getChangesFromToolArgs<TChange>(toolArgs, config.normalizeChanges),
    [toolArgs, config.normalizeChanges],
  );

  useEffect(() => {
    if (
      !isActive ||
      interactionsDisabled ||
      responseInFlightRef.current ||
      !plan?.userResponse ||
      plan.agentAcknowledged ||
      plan.toolCallId !== toolCallId
    ) {
      return;
    }
    const response = plan.userResponse;
    responseInFlightRef.current = true;
    void (async () => {
      const result =
        response === 'confirmed'
          ? await onApproveToolUse(true, buildNewArgsFromPlan(plan, toolArgs))
          : await onApproveToolUse(false);
      responseInFlightRef.current = false;
      if (result === 'delivered') {
        responseRef.current = response;
        setPlan((prev) =>
          prev?.toolCallId === toolCallId && prev.userResponse === response
            ? { ...prev, agentAcknowledged: true }
            : prev,
        );
      } else if (result === 'failed') {
        setPlan((prev) =>
          prev?.toolCallId === toolCallId && prev.userResponse === response
            ? { ...prev, userResponse: undefined }
            : prev,
        );
      }
    })();
  }, [
    isActive,
    interactionsDisabled,
    onApproveToolUse,
    plan,
    setPlan,
    toolArgs,
    toolCallId,
  ]);

  const mergePlanWithToolArgs = useCallback(
    (
      prev: AgentTestConfigPlan<TChange> | null | undefined,
      extras: Partial<AgentTestConfigPlan<TChange>> = {},
    ): AgentTestConfigPlan<TChange> => {
      const base = planFromToolArgs<TChange>(
        toolArgs,
        toolCallId,
        config.normalizeChanges,
      );
      if (prev?.toolCallId !== toolCallId) {
        return { ...base, ...extras };
      }
      return {
        ...base,
        ...prev,
        // Keep wizard edits / selection when present; recover from tool args
        // if a prior parse left the plan empty (e.g. stringified `changes`).
        changes: prev.changes.length > 0 ? prev.changes : base.changes,
        skipSummary: prev.skipSummary ?? base.skipSummary,
        ...extras,
      };
    },
    [toolArgs, toolCallId, config.normalizeChanges],
  );

  const handleReviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const isReadOnly = !isActive;
      setPlan((prev) => {
        if (prev?.toolCallId === toolCallId) {
          return mergePlanWithToolArgs(prev, isReadOnly ? { isReadOnly } : {});
        }
        const restoredResponse: PlanApprovalStatus | undefined =
          responseRef.current ??
          resolveCompletedStatus({
            isActive: false,
            didAwaitApproval,
            resultIsError,
          });
        if (restoredResponse) {
          return mergePlanWithToolArgs(null, {
            userResponse: restoredResponse,
            agentAcknowledged: true,
            isReadOnly,
          });
        }
        return mergePlanWithToolArgs(null, { isReadOnly });
      });
      setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
      navigate(config.previewPath);
    },
    [
      navigate,
      setChatPanel,
      setPlan,
      toolCallId,
      resultIsError,
      didAwaitApproval,
      isActive,
      config.previewPath,
      mergePlanWithToolArgs,
    ],
  );

  const handleApprove = useCallback(() => {
    setPlan((prev) =>
      mergePlanWithToolArgs(prev, { userResponse: 'confirmed' as const }),
    );
    closePreviewIfOpen();
  }, [setPlan, closePreviewIfOpen, mergePlanWithToolArgs]);

  const handleReject = useCallback(() => {
    setPlan((prev) =>
      mergePlanWithToolArgs(prev, { userResponse: 'rejected' as const }),
    );
    closePreviewIfOpen();
  }, [setPlan, closePreviewIfOpen, mergePlanWithToolArgs]);

  const completedFromResult = resolveCompletedStatus({
    isActive,
    didAwaitApproval,
    resultIsError,
  });

  const status: PlanApprovalStatus | undefined =
    responseRef.current ??
    (plan?.agentAcknowledged && plan.toolCallId === toolCallId
      ? plan.userResponse
      : undefined) ??
    completedFromResult;

  const previewChanges = changes.slice(0, MAX_PREVIEW_ROWS);
  const remainingCount = changes.length - previewChanges.length;
  const skipSummary = formatSkippedPlanSummary(toolArgs?.skipped);

  const headerLabel = useMemo(
    () => config.getHeaderLabel(status, operation, changes.length),
    [config, status, changes.length, operation],
  );

  const approveLabel = config.getApproveLabel(
    operation,
    changes,
    isDataTestsEnabled,
  );

  const showActions = isActive && !status;

  return (
    <ApprovalCard $status={status}>
      <ApprovalCardHeader $status={status}>
        <EditIcon width={16} height={16} />
        <ApprovalCardTitle $status={status}>{headerLabel}</ApprovalCardTitle>
      </ApprovalCardHeader>
      <ApprovalCardBody>
        <ChangesGrid>
          {previewChanges.map((change) => (
            <React.Fragment key={getAgentTestChangeRowId(change)}>
              <TestIdCell>{config.formatChangeLabel(change)}</TestIdCell>
              <Typography
                sx={{
                  fontSize: fontSize('sm'),
                  color: 'content.tertiary',
                  wordBreak: 'break-word',
                }}
              >
                {config.formatChangeSummary(change, operation)}
              </Typography>
            </React.Fragment>
          ))}
        </ChangesGrid>

        {remainingCount > 0 && (
          <Typography
            sx={{ fontSize: fontSize('sm'), color: 'content.tertiary' }}
          >
            +{remainingCount} more
          </Typography>
        )}

        {skipSummary && (
          <Typography
            sx={{ fontSize: fontSize('sm'), color: 'content.tertiary' }}
          >
            {skipSummary}
          </Typography>
        )}

        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <ViewLink onClick={handleReviewClick}>
            View full plan
            <SvgIcon
              strokeColor="accent.primary"
              width={10}
              style={{ marginTop: themeTokens.space.hairline }}
            >
              <ArrowRightIcon />
            </SvgIcon>
          </ViewLink>

          {showActions && (
            <Flex sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                size="extraSmall"
                disabled={interactionsDisabled}
                onClick={handleReject}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="extraSmall"
                disabled={interactionsDisabled}
                onClick={handleApprove}
              >
                {approveLabel}
              </Button>
            </Flex>
          )}
        </Flex>
      </ApprovalCardBody>
    </ApprovalCard>
  );
}

export function createPlanApprovalRenderer<
  TChange extends AgentTestChangeRowIdentifiers,
>(config: PlanRendererConfig<TChange>) {
  return ({
    invocation,
    onApproveToolUse,
    isActive,
    interactionsDisabled,
  }: ApprovalToolRendererProps) => {
    if (neverReachedApproval(invocation)) {
      return null;
    }
    return (
      <ApplyTestConfigPlanToolRendererInner<TChange>
        config={config}
        toolArgs={invocation.args ?? {}}
        toolCallId={invocation.toolCallId}
        resultIsError={extractToolResultIsError(invocation.result)}
        didAwaitApproval={invocation.didAwaitApproval}
        onApproveToolUse={onApproveToolUse}
        isActive={isActive}
        interactionsDisabled={interactionsDisabled}
      />
    );
  };
}
